import { lazy, Suspense, useEffect } from "react";
import { Navigate, Route, Routes, useNavigate } from "react-router-dom";
import AnnouncementBanner from "./components/AnnouncementBanner";
import PrivacyGate from "./components/PrivacyGate";
import TourHost from "./components/TourHost";
import UpdateGate from "./components/UpdateGate";
import { useAnalytics } from "./lib/analytics";
import { initNative } from "./lib/native";
import { initNotifications } from "./lib/notifications";
import LoginPage from "./routes/login";
import HomePage from "./routes/home";

// Heavy routes are code-split so the cold-start bundle stays small — but all
// chunks are prefetched during idle time right after first paint (see
// preloadRoutes below), so navigation never has to wait for a download.
const loadBudget = () => import("./routes/budget");
const loadTransactions = () => import("./routes/transactions");
const loadReconcile = () => import("./routes/reconcile");
const loadSparziele = () => import("./routes/sparziele");
const loadGoals = () => import("./routes/goals");
const loadHistorie = () => import("./routes/historie");
const loadDepot = () => import("./routes/depot");
const loadZuordnung = () => import("./routes/zuordnung");
const loadSetup = () => import("./routes/setup");
const loadEinstellungen = () => import("./routes/einstellungen");
const loadBilling = () => import("./routes/billing");

const BudgetPage = lazy(loadBudget);
const TransactionsPage = lazy(loadTransactions);
const ReconcilePage = lazy(loadReconcile);
const SparzielePage = lazy(loadSparziele);
const GoalsPage = lazy(loadGoals);
const HistoriePage = lazy(loadHistorie);
const DepotPage = lazy(loadDepot);
const ZuordnungPage = lazy(loadZuordnung);
const SetupPage = lazy(() => loadSetup().then((m) => ({ default: m.default })));
const SetupJoinPage = lazy(() => loadSetup().then((m) => ({ default: m.SetupJoinPage })));
const EinstellungenPage = lazy(loadEinstellungen);
const BillingSuccessPage = lazy(() =>
  loadBilling().then((m) => ({ default: m.BillingSuccessPage })),
);
const BillingCancelPage = lazy(() =>
  loadBilling().then((m) => ({ default: m.BillingCancelPage })),
);

/** Warm every route chunk once the browser is idle. Failures are fine —
 *  the lazy() import on first visit remains the source of truth. */
function preloadRoutes() {
  const loaders = [
    loadBudget, loadTransactions, loadReconcile, loadSparziele, loadGoals,
    loadHistorie, loadDepot, loadZuordnung, loadEinstellungen, loadSetup,
    loadBilling,
  ];
  const run = () => loaders.forEach((l) => { l().catch(() => {}); });
  if ("requestIdleCallback" in window) {
    window.requestIdleCallback(run, { timeout: 4000 });
  } else {
    setTimeout(run, 1500);
  }
}

/** In-app navigation requested from outside the React tree (e.g. the
 * notification-tap listener in lib/notifications.ts). Event detail = path. */
function ExternalNavListener() {
  const navigate = useNavigate();
  useEffect(() => {
    const handler = (e: Event) => {
      const path = (e as CustomEvent).detail;
      // Same-origin app paths only: "//host" and backslashes ("/\host") are
      // treated as external URLs by browsers (react-router open-redirect,
      // GHSA-wrjc-x8rr-h8h6), so reject them.
      if (
        typeof path === "string" &&
        path.startsWith("/") &&
        !path.startsWith("//") &&
        !path.includes("\\")
      )
        navigate(path);
    };
    window.addEventListener("logbuch:navigate", handler);
    return () => window.removeEventListener("logbuch:navigate", handler);
  }, [navigate]);
  return null;
}

function RouteFallback() {
  // Plain dark screen; the spinner fades in only after 300ms so a fast chunk
  // load (the normal case — chunks are idle-preloaded) never flashes UI.
  return (
    <div className="min-h-screen grid place-items-center bg-ink-950">
      <div
        className="route-spinner h-6 w-6 rounded-full border-2 border-ink-700 border-t-brand-400"
        aria-label="Lädt"
      />
    </div>
  );
}

export default function App() {
  useAnalytics();
  useEffect(() => {
    initNative();
    // Fire-and-forget — permission prompt + scheduling is async but the
    // rest of the app doesn't need to wait for it.
    initNotifications();
    preloadRoutes();
  }, []);
  return (
    <>
    <ExternalNavListener />
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/setup" element={<SetupPage />} />
        <Route path="/setup/join/:token" element={<SetupJoinPage />} />
        <Route path="/" element={<HomePage />} />
        <Route path="/budget" element={<BudgetPage />} />
        <Route path="/transactions" element={<TransactionsPage />} />
        <Route path="/reconcile" element={<ReconcilePage />} />
        <Route path="/sparziele" element={<SparzielePage />} />
        <Route path="/goals" element={<GoalsPage />} />
        <Route path="/historie" element={<HistoriePage />} />
        <Route path="/depot" element={<DepotPage />} />
        <Route path="/zuordnung" element={<ZuordnungPage />} />
        <Route path="/einstellungen" element={<EinstellungenPage />} />
        {/* /billing now lives as a section inside /einstellungen — redirect any
            deep-links (auth-gate, old bookmarks) over there. */}
        <Route path="/billing" element={<Navigate to="/einstellungen" replace />} />
        <Route path="/billing/success" element={<BillingSuccessPage />} />
        <Route path="/billing/cancel" element={<BillingCancelPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
    <TourHost />
    <UpdateGate />
    <PrivacyGate />
    <AnnouncementBanner />
    </>
  );
}
